import React, { useState, useEffect } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { useLanguage } from "../tools/LanguageContext";
import { Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

function Subscription() {
  const { translations } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("status");
  const [vipExpiration, setVipExpiration] = useState(null);
  const [renewalDuration, setRenewalDuration] = useState("");
  const [message, setMessage] = useState("");
  const [price, setPrice] = useState(0);

  useEffect(() => {
    AOS.init();

    if (renewalDuration === "7d") {
      setPrice(7);
    } else if (renewalDuration === "30d") {
      setPrice(15);
    } else {
      setPrice(0);
    }

    const fetchVipStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert(translations.alertTokenExpired);
          // Redirect στο home
          navigate("/");

          return;
        }

        const response = await axios.post("https://artofbetting.gr/api/get_vip_status",  {}, {
          headers: {
            "Authorization": token,
          }});
        setVipExpiration(response.data.vip_valid_until || null);
      } catch (error) {
        if (error.response?.status === 401) {
          alert(translations.alertTokenExpired);
          // Redirect στο home
          localStorage.removeItem("token");
          navigate("/");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchVipStatus();
  }, [navigate, translations.alertTokenExpired, renewalDuration]);

  const createOrder = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    return fetch("https://artofbetting.gr/api/create-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": token,
      },
      body: JSON.stringify({ price }), // Αποστολή της τιμής
    })
      .then((res) => {
        if (!res.ok) { // Αν το status code δεν είναι 2xx (επιτυχία)
          throw new Error(`HTTP error! Status: ${res.status}`); // Ρίχνουμε σφάλμα με το status
        }
        return res.json(); // Αν είναι 2xx, προχωράμε με το JSON
      })
      .then((data) => data.id) // Επιστροφή του id από την απόκριση
      .catch((error) => {
        console.error("Error creating order:", error);
        // Έλεγχος αν το σφάλμα είναι λόγω HTTP status και το χειρισμός του
        if (error.message.includes("Status: 401")) {
          alert(translations.alertTokenExpired);
          // Redirect στο home
          localStorage.removeItem("token");
          navigate("/");
        } else {
          setMessage(translations.failedPayment);
        }
      });
  };

  const onApprove = (data) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    return fetch(`https://artofbetting.gr/api/capture-order/${data.orderID}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": token,
      },
      body: JSON.stringify({ duration: renewalDuration }), // Αποστολή duration στο backend
    })
    .then((res) => {
      if (!res.ok) { // Αν το status code δεν είναι 2xx (επιτυχία)
        throw new Error(`HTTP error! Status: ${res.status}`); // Ρίχνουμε σφάλμα με το status
      }
      return res.json(); // Αν είναι 2xx, προχωράμε με το JSON
    })
    .then((details) => {
      setMessage(translations.successPayment);
      alert(translations.checkStatus);
      setVipExpiration(details.vip_valid_until); // Ενημέρωση ημερομηνίας λήξης
    })
    .catch((error) => {
      console.error("Error capturing order:", error);
      // Έλεγχος αν το σφάλμα είναι λόγω HTTP status και το χειρισμός του
      if (error.message.includes("Status: 401")) {
        alert(translations.alertTokenExpired);
        // Redirect στο home
        localStorage.removeItem("token");
        navigate("/");
      } else {
        setMessage(translations.failedPayment);
      }
    });
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen p-4">
      <header className="flex justify-between items-center p-4 bg-gray-800 mb-6 rounded-lg">
        <h1 className="text-3xl font-bold">ART OF BETTING</h1>
        <nav className="flex space-x-4">
          <Link to="/">
            <button className="bg-green-800 px-4 py-2 rounded hover:bg-green-900">
              {translations.home}
            </button>
          </Link>
        </nav>
      </header>

      <h2 className="text-3xl font-bold mb-4">{translations.subscription}</h2>

      <div className="flex space-x-4 mt-6">
        <button
          onClick={() => setActiveTab("status")}
          className={`px-4 py-2 rounded ${
            activeTab === "status" ? "bg-green-800" : "bg-gray-700"
          }`}
        >
          {translations.subscriptionStatus}
        </button>
        <button
          onClick={() => setActiveTab("renew")}
          className={`px-4 py-2 rounded ${
            activeTab === "renew" ? "bg-green-800" : "bg-gray-700"
          }`}
        >
          {translations.renewSubscription}
        </button>
      </div>

      <div className="mt-6 bg-gray-800 p-4 rounded shadow-lg">
        {activeTab === "status" ? (
          <div>
            <h2 className="text-3xl font-bold mb-4">{translations.subscriptionStatus}</h2>
            {vipExpiration ? (
              <>
                <p className="mb-4">
                  {translations.subscriptionValidUnitl}{" "}
                  <strong>
                    {new Date(vipExpiration).toLocaleString("el-GR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </strong>
                </p>
                <a href="https://t.me/ArtOfBettingBot" target="_blank" rel="noreferrer">
                  <button className="px-4 py-2 rounded bg-green-800">{translations.talkToBot}</button>
                </a>
              </>
            ) : loading ? (
              <p>{translations.loading}</p>
            ) : (
              <p>{translations.noSubscription}</p>
            )}
          </div>
        ) : (
          <div>
            <h2 className="text-3xl font-bold mb-4">{translations.renewSubscription}</h2>
            <div className="mb-4">
              <label htmlFor="duration" className="block text-lg mb-2">
                {translations.renewalDuration}
              </label>
              <select
                id="duration"
                value={renewalDuration}
                onChange={(e) => { setRenewalDuration(e.target.value); setMessage("")}}
                className="bg-gray-700 text-white p-2 rounded w-full"
              >
                <option value="">{translations.selectDuration}</option>
                <option value="7d">{translations.sevenDays}</option>
                <option value="30d">{translations.thirtyDays}</option>
              </select>
              {price > 0 && (
                <div
                  className="flex items-center justify-center"
                  data-aos="zoom-in"
                  data-aos-duration="1000"
                  data-aos-delay="200"
                >
                  <p className="mt-2 text-green-400">
                    {translations.price}: {price}€
                  </p>
                </div>
              )}
            </div>
            {price > 1000 && (
              <div
                className="flex items-center justify-center"
                data-aos="zoom-in"
                data-aos-duration="1000"
                data-aos-delay="200"
              >
                <div className="bg-gray-700 w-96 p-6 rounded-lg shadow-lg">
                  <PayPalScriptProvider options={{ "client-id": "AeBDR5pxwd6MwQrLq8kr2-R1850L6T2SNFPn-7molgiC0C4QWV1upX4Z3lwPtYc7cn1A17-nO5Xk5yVf", currency: "EUR" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "blue", shape: "rect", label: "paypal", tagline: false }}
                      createOrder={createOrder}
                      onApprove={onApprove}
                    />
                  </PayPalScriptProvider>
                </div>
              </div>
            )}
            {price > 0 && (
              <div
                className="flex items-center justify-center"
                data-aos="zoom-in"
                data-aos-duration="1000"
                data-aos-delay="200"
              >
                <div className="bg-gray-700 w-96 p-6 rounded-lg shadow-lg">
                  <a href="https://t.me/Mrarber" target="_blank" rel="noreferrer">
                    <button className="px-4 py-2 mb-4 rounded bg-green-800">Επικοινωνήστε με τον Mr. Arber για τη πληρωμή</button>
                  </a>
                  <p className="text-center">
                    Revolut -
                    Iris -
                    Paysafe -
                    Iban
                  </p>
                </div>
              </div>
            )}
            {message &&
              <div className="flex items-center justify-center">
                <p className="mt-4 text-green-400">{message}</p>
              </div>
            }
          </div>
        )}
      </div>

      <footer className="relative w-full bg-gray-900 py-8">
        <div className="flex flex-col items-center justify-between w-full">
          <p className="text-xl font-bold border-b border-t p-4 mb-8">© Copyright 2025 Art Of Betting</p>
          <p className="text-lg font-bold border-b border-t p-2 mb-8 text-center">
            <span className="font-semibold">Διαφημιστειτε εδώ:</span>{' '}
            <a href="mailto:artofbetting2025@gmail.com" className="hover:underline">
              artofbetting2025@gmail.com
            </a>
          </p>

          <div className="flex items-center justify-between w-2/5 mb-8">
            <a href="https://www.kethea.gr/" target="_blank" rel="noopener noreferrer" className="w-1/5 mr-8">
              <img
                src="images/21.svg"
                alt="21+"
                className="w-full h-auto"
              />
            </a>

            <a href="https://www.kethea.gr/" target="_blank" rel="noopener noreferrer" className="w-4/5">
              <img
                src="images/kethea.svg"
                alt="kethea"
                className="w-full h-auto"
              />
            </a>
          </div>

          <a href="https://certifications.gamingcommission.gov.gr/publicRecordsOnline/SitePages/AffiliatesOnline.aspx" target="_blank" rel="noopener noreferrer">
            <img
              src="images/eeep.png"
              alt="eeep"
              className="w-full h-auto border-b border-t p-4"
            />
          </a>
        </div>
      </footer>
    </div>
  );
}

export default Subscription;
